import { useRef, useCallback } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { NumericInput } from '../strategy/NumericInput';
import { computeDeficit } from './buildingsPercent';

interface BuildingRowProps {
  buildingId: string;
  name: string;
  owned: number;
  inProgress: number;
  totalLand: number;
  totalBuildings: number;
  be: number;
  targetPct: number | null;
  buildQty: number;
  remainingBarren: number;
  highlighted: boolean;
  onBuildQtyChange: (buildingId: string, value: number) => void;
  onFillDeficit: (buildingId: string) => void;
}

export function BuildingRow({
  buildingId,
  name,
  owned,
  inProgress,
  totalLand,
  targetPct,
  buildQty,
  remainingBarren,
  highlighted,
  onBuildQtyChange,
  onFillDeficit,
}: BuildingRowProps) {
  const highlightRef = useRef<HTMLDivElement>(null);

  const { deficit } = computeDeficit({ targetPct, totalLand, owned, inProgress, remainingBarren });
  const canFill = deficit > 0 && remainingBarren > 0;

  const handleChange = useCallback(
    (val: number) => {
      onBuildQtyChange(buildingId, Math.max(0, val));
    },
    [buildingId, onBuildQtyChange]
  );

  const handleFill = useCallback(() => {
    onFillDeficit(buildingId);
  }, [buildingId, onFillDeficit]);

  // Flash highlight style (only applies background when active, no transition on hover)
  const highlightStyle: React.CSSProperties = highlighted
    ? {
        backgroundColor: `${COLORS.text.primary}0f`,
      }
    : {};

  return (
    <div
      ref={highlightRef}
      className="grid items-center px-3 py-1.5 building-row-highlight"
      style={{
        gridTemplateColumns: '1fr 64px 64px 88px',
        ...highlightStyle,
      }}
    >
      {/* Col 1: Name */}
      <span className="text-sm truncate" style={{ color: COLORS.text.primary }}>
        {name}
      </span>

      {/* Col 2: Have */}
      <span className="text-center font-mono text-sm" style={{ color: COLORS.text.secondary }}>
        {formatNumber(owned)}
      </span>

      {/* Col 3: In Progress */}
      <span className="text-center font-mono text-sm" style={{ color: inProgress > 0 ? COLORS.text.secondary : COLORS.text.tertiary }}>
        {inProgress > 0 ? formatNumber(inProgress) : ''}
      </span>

      {/* Col 4: Build input + fill button (fixed-width slot for button) */}
      <div className="flex items-center justify-end gap-1">
        <NumericInput
          value={buildQty}
          max={remainingBarren + buildQty}
          onChange={handleChange}
          className="w-14"
        />
        <div style={{ width: '26px', height: '26px', flexShrink: 0 }}>
          {canFill && (
            <button
              onClick={handleFill}
              aria-label={`Fill ${name} deficit of ${deficit} acres`}
              title={`Fill ${name} deficit of ${deficit} acres`}
              style={{
                width: '26px',
                height: '26px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.sectionHeader,
                color: '#B89878',
                cursor: 'pointer',
              }}
            >
              ⚡
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
